import { useMutation, useQuery } from '@apollo/client'
import { useUrlSearchParams } from '@dai/common'
import styled from '@emotion/styled'
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material'
import LoadingButton from '@mui/lab/LoadingButton'
import {
  Box,
  Button,
  Chip as MuiChip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { green, yellow } from '@mui/material/colors'
import { spacing, SpacingProps } from '@mui/system'
import { EditIcon, SendIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useDebounce } from 'use-debounce'

import { useRefresh } from '@/contexts/RefreshContext'
import {
  GET_TENANT_USERS,
  GetTenantUsersQuery,
  GetTenantUsersQueryVariables,
  InputMaybe,
  RESTORE_USER,
  RestoreUserMutation,
  RestoreUserMutationVariables,
  SOFT_DELETE_USER,
  SoftDeleteUserMutation,
  SoftDeleteUserMutationVariables,
  UserFilter,
  UserOrder,
} from '@/graphql'
import useLoggedInUser from '@/hooks/useLoggedInUser'
import { useTablePageState } from '@/hooks/useTablePageState'
import useT from '@/i18n/useT'
import { LocalizationHelpers } from '@/utils/localization.helpers'

import DeliciousTable, { HeadCell } from '../components/DeliciousTable'

export interface ChipProps extends SpacingProps {
  status: 'active' | 'inactive'
}

export const Chip = styled(MuiChip)<ChipProps>`
  ${spacing};
  background: ${props =>
    props.status === 'inactive' ? yellow[700] : green[700]};
  color: ${props =>
    props.status === 'active' && props.theme.palette.common.white};
`

type User = GetTenantUsersQuery['tenantUsers']['results'][0]

function UserList() {
  const {
    handle: { handleNavTo },
  } = useUrlSearchParams()
  const { t } = useT()
  const { page, rowsPerPage, setPage, setRowsPerPage } = useTablePageState({
    initialRowsPerPage: 25,
  })
  const { user: currentUser } = useLoggedInUser()

  const [order, setOrder] = useState<'ASC' | 'DESC'>('ASC')
  const [orderBy, setOrderBy] = useState<keyof UserOrder>('firstName')

  const [showDeletedUsers, setShowDeletedUsers] = useState(false)
  const [deleteUser, setDeleteUser] = useState<User | null>(null)
  const [restoreUser, setRestoreUser] = useState<User | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm] = useDebounce(searchTerm, 800)

  const [handleDeleteUser, { loading: deletingUser }] = useMutation<
    SoftDeleteUserMutation,
    SoftDeleteUserMutationVariables
  >(SOFT_DELETE_USER)

  const [handleRestoreUser, { loading: restoringUser }] = useMutation<
    RestoreUserMutation,
    RestoreUserMutationVariables
  >(RESTORE_USER)

  const handleRequestSort = (property: keyof UserOrder) => {
    const isAsc = orderBy === property && order === 'ASC'
    setOrder(isAsc ? 'DESC' : 'ASC')
    setOrderBy(property)
  }

  const filters: InputMaybe<UserFilter> = useMemo(
    () => ({
      isSuperuser: { exact: false },
      NOT: { email: { iRegex: '^system-.*@deliciousai.com$' } },
      AND: {
        firstName: { iStartsWith: debouncedSearchTerm },
        OR: {
          lastName: { iStartsWith: debouncedSearchTerm },
          OR: { email: { iContains: debouncedSearchTerm } },
        },
      },
    }),
    [debouncedSearchTerm],
  )

  const queryFilters = useMemo(
    () => ({
      ...filters,
      ...(showDeletedUsers ? {} : { dateRemoved: { isNull: true } }),
    }),
    [filters, showDeletedUsers],
  )

  const buildQueryVars = (extraFilters?: UserFilter) => ({
    order: { [orderBy]: order },
    filters: { ...filters, ...(extraFilters ?? {}) },
    pagination: { limit: rowsPerPage, offset: page * rowsPerPage },
  })

  const getRefetchQueries = () => [
    {
      query: GET_TENANT_USERS,
      variables: buildQueryVars({ dateRemoved: { isNull: true } }),
    },
    {
      query: GET_TENANT_USERS,
      variables: buildQueryVars(),
    },
  ]

  const userData = useQuery<GetTenantUsersQuery, GetTenantUsersQueryVariables>(
    GET_TENANT_USERS,
    {
      notifyOnNetworkStatusChange: true,
      variables: {
        order: { [orderBy]: order },
        filters: queryFilters,
        pagination: {
          limit: rowsPerPage,
          offset: page * rowsPerPage,
        },
      },
    },
  )
  useRefresh(userData.refetch)

  const headCells: HeadCell<UserOrder>[] = [
    {
      id: 'firstName',
      alignment: 'left',
      label: t('userList.columns.firstName'),
      sortKey: 'firstName',
    },
    {
      id: 'lastName',
      alignment: 'left',
      label: t('userList.columns.lastName'),
      sortKey: 'lastName',
    },
    {
      id: 'email',
      alignment: 'left',
      label: t('userList.columns.email'),
      sortKey: 'email',
    },
    { id: 'groups', alignment: 'left', label: t('userList.columns.role') },
    {
      id: 'jobTitle',
      alignment: 'left',
      label: t('userList.columns.jobTitle'),
      sortKey: 'jobTitle',
    },
    {
      id: 'pin',
      alignment: 'left',
      label: t('userList.columns.pin'),
      tooltip: t('userList.columns.pinTooltip'),
    },
    { id: 'actions', alignment: 'right', label: t('userList.columns.actions') },
  ]

  const renderRow = (user: User, index: number) => {
    const userRoles = new Set(user?.groups?.map(group => group.name) || [])
    const currentUserManageableRoles = new Set(
      currentUser?.groups?.flatMap(group =>
        group.manageableRoles.map(group => group.name),
      ) || [],
    )
    const canEdit =
      userRoles.size > 0 &&
      Array.from(userRoles).every(role => currentUserManageableRoles.has(role))

    return (
      <TableRow
        hover
        role="checkbox"
        tabIndex={-1}
        key={`${user.id}-${index}`}
        sx={{
          backgroundColor: user.dateRemoved ? 'action.hover' : undefined,
        }}
      >
        <TableCell>
          <Box ml={3}>{user?.firstName || 'N/A'}</Box>
        </TableCell>
        <TableCell>
          <Box ml={3}>{user?.lastName || 'N/A'}</Box>
        </TableCell>
        <TableCell align="left">{user?.email}</TableCell>
        <TableCell align="left">
          {user?.groups
            .map(({ name }) => LocalizationHelpers.localizedGroupName(t, name))
            .sort()
            .join(', ') || 'N/A'}
        </TableCell>
        <TableCell align="left">{user?.jobTitle}</TableCell>
        <TableCell align="left">
          {user?.pinNumber ?? t('userList.pinNumber.emptyLabel')}
        </TableCell>
        <TableCell align="right">
          {canEdit && user.dateRemoved ? (
            <Button
              onClick={() => setRestoreUser(user)}
              disabled={!!restoreUser || restoringUser}
            >
              {t('userList.actions.restore')}
            </Button>
          ) : (
            <>
              {canEdit && (
                <IconButton
                  onClick={() => {
                    handleNavTo('/admin/users/edit-user', {
                      replaceParams: { id: user?.id },
                    })
                  }}
                  size="large"
                >
                  <EditIcon />
                </IconButton>
              )}

              {canEdit && user?.id !== currentUser?.id && (
                <IconButton
                  onClick={() => {
                    setDeleteUser(user)
                  }}
                  size="large"
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </>
          )}
        </TableCell>
      </TableRow>
    )
  }

  return (
    <>
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 3, pb: 3, pt: 6 }}
      >
        <TextField
          id="userSearch"
          type="text"
          name="userSearch"
          label={t('userList.searchUser')}
          value={searchTerm}
          fullWidth
          placeholder={t('userList.searchUserHelperText')}
          onChange={e => setSearchTerm(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton onClick={() => setSearchTerm('')} size="small">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        <DeliciousTable<User, UserOrder>
          title={t('userList.title')}
          size="small"
          data={userData?.data?.tenantUsers.results || []}
          headCells={headCells}
          renderRow={renderRow}
          loading={userData.loading}
          error={userData.error?.message}
          totalCount={userData?.data?.tenantUsers.totalCount || 0}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          handleRequestSort={handleRequestSort}
          order={order === 'ASC' ? 'asc' : 'desc'}
          orderBy={orderBy}
          RightHeaderComponent={
            <Stack direction="row" spacing={2}>
              <Button onClick={() => setShowDeletedUsers(!showDeletedUsers)}>
                {showDeletedUsers
                  ? t('userList.actions.hideDeletedUsers')
                  : t('userList.actions.showDeletedUsers')}
              </Button>
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={() =>
                  handleNavTo('/admin/users', {
                    replaceParams: { tab: 'invites' },
                  })
                }
              >
                {t('userList.actions.invite')}
              </Button>
            </Stack>
          }
        />
      </Box>
      <Dialog
        open={deleteUser !== null}
        onClose={() => setDeleteUser(null)}
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h3" gutterBottom>
            {t('userList.actions.delete')}
          </Typography>
          <Typography>{t('userList.deleteUserConfirmation')}</Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ paddingLeft: 5 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              {deleteUser?.firstName} {deleteUser?.lastName}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {deleteUser?.email}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteUser(null)}
            variant="outlined"
            disabled={deletingUser}
          >
            {t('common.cancel')}
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={deletingUser}
            loadingPosition="end"
            onClick={async () => {
              if (deleteUser) {
                try {
                  await handleDeleteUser({
                    variables: { id: deleteUser.id },
                    refetchQueries: getRefetchQueries(),
                    awaitRefetchQueries: true,
                  })
                } catch (e) {
                  console.error(e)
                }
              }
              setDeleteUser(null)
            }}
            color="error"
          >
            {t('userList.actions.delete')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
      <Dialog
        open={restoreUser !== null}
        onClose={() => !restoringUser && setRestoreUser(null)}
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h3" gutterBottom>
            {t('userList.actions.restore')}
          </Typography>
          <Typography>{t('userList.restoreUserConfirmation')}</Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ paddingLeft: 5 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              {restoreUser?.firstName} {restoreUser?.lastName}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {restoreUser?.email}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRestoreUser(null)}
            variant="outlined"
            disabled={restoringUser}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            loading={restoringUser}
            loadingPosition="end"
            onClick={async () => {
              if (restoreUser) {
                try {
                  await handleRestoreUser({
                    variables: { id: restoreUser.id },
                    refetchQueries: getRefetchQueries(),
                    awaitRefetchQueries: true,
                  })
                } catch (e) {
                  console.error(e)
                }
              }
              setRestoreUser(null)
            }}
            color="primary"
          >
            {t('userList.actions.restore')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default UserList
